import { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";

import {
  useVisionUIController,
  setTransparentSidenav,
  setFixedNavbar,
  setSidenavColor,
  setCurrency,
  setFiscalYearStart,
  setDateFormat,
  setDefaultLandingPage,
  setNotificationBadge,
  setAutoRefreshInterval,
  setDefaultVatRate,
  setServiceDefault,
} from "context";
import {
  listMasterServices, listTenantUsers,
  getEmailTemplates, saveEmailTemplate,
  getFeeSchedule, updateFeeSchedule,
} from "services/tenantUserAdminService";
import { getSuperAdminConfig, updateSuperAdminConfig } from "services/visitorApiService";
import { useAuthz } from "context/AuthzContext";
import { TC, FS, FW } from "../../tokens/text";

const CARD_BG = "#ffffff";
const CARD_BORDER = "1px solid #d8e6f7";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const selectSx = {
  color: `${TC.primary} !important`,
  fontSize: FS.sm,
  minWidth: 160,
  background: "#f4f8ff",
  border: "1px solid #d8e6f7",
  borderRadius: "6px",
  ".MuiOutlinedInput-notchedOutline": { border: "none" },
  ".MuiSvgIcon-root": { color: TC.secondary },
  ".MuiSelect-select": { py: "7px", px: "12px" },
};

const sidenavColors = ["primary", "info", "success", "warning", "error"];

// ── Card with icon header ────────────────────────────────────────────────────

function SectionCard({ icon, iconColor, title, description, children }) {
  return (
    <VuiBox
      sx={{
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: "20px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Card header */}
      <VuiBox px={3} pt={3} pb={2}>
        <VuiBox display="flex" alignItems="center" gap={1.5} mb={1}>
          <VuiBox
            sx={{
              width: 36, height: 36, borderRadius: "10px",
              background: `${iconColor}22`,
              border: `1px solid ${iconColor}44`,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: iconColor, fontSize: "18px !important" }}>{icon}</Icon>
          </VuiBox>
          <VuiBox>
            <VuiTypography sx={{ color: TC.primary, fontSize: "14px", fontWeight: 700, lineHeight: 1.3 }}>
              {title}
            </VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: "11px", lineHeight: 1.4 }}>
              {description}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      </VuiBox>

      <Divider sx={{ borderColor: "#d8e6f7", m: 0 }} />

      {/* Card body */}
      <VuiBox px={3} py={2} display="flex" flexDirection="column" flex={1}>
        {children}
      </VuiBox>
    </VuiBox>
  );
}

// ── Row: label left, control right ──────────────────────────────────────────

function SettingRow({ label, hint, children, last }) {
  return (
    <>
      <VuiBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        py={1.8}
        sx={{ flexWrap: "wrap" }}
      >
        <VuiBox sx={{ minWidth: 0, flex: 1 }}>
          <VuiTypography sx={{ color: TC.primary, fontSize: "13px", fontWeight: 600, lineHeight: 1.4 }}>
            {label}
          </VuiTypography>
          {hint && (
            <VuiTypography sx={{ color: TC.secondary, fontSize: "11px", lineHeight: 1.5, mt: 0.3 }}>
              {hint}
            </VuiTypography>
          )}
        </VuiBox>
        <VuiBox sx={{ flexShrink: 0 }}>
          {children}
        </VuiBox>
      </VuiBox>
      {!last && <Divider sx={{ borderColor: "#d8e6f7", m: 0 }} />}
    </>
  );
}

// ── Email template definitions (static, outside component) ───────────────────

const EMAIL_TEMPLATE_DEFS = [
  {
    key: "ad01_confirm_address",
    label: "AD01 — Confirm new registered address",
    defaultSubject: "Confirming your new registered address — {{company}}",
    defaultBody:    "Dear Client,\n\nWe are writing to confirm the new registered address for {{company}}.\n\nNew address: {{address}}\n\nCould you please confirm this is correct so we can proceed with filing form AD01 with Companies House?\n\nKind regards",
  },
  {
    key: "ad01_update_records",
    label: "AD01 — Address change filed notification",
    defaultSubject: "Address change filed — {{company}}",
    defaultBody:    "Dear Client,\n\nWe are pleased to confirm that the registered address change (AD01) for {{company}} has been filed with Companies House and our records have been updated.\n\nNew registered address: {{address}}\n\nPlease let us know if you have any questions.\n\nKind regards",
  },
];

// ── Fee service definitions ──────────────────────────────────────────────────

const FEE_SERVICE_DEFS = [
  { id: "vat",                 label: "VAT Returns" },
  { id: "payroll",             label: "Payroll (PAYE)" },
  { id: "accounts",            label: "Accounts Prep" },
  { id: "corporation_tax",     label: "CT600" },
  { id: "self_assessment",     label: "Self Assessment (SA)" },
  { id: "company_secretarial", label: "Confirmation Statement (CS01)" },
  { id: "mtd_quarterly",       label: "MTD Quarterly" },
  { id: "ad01_address_change", label: "Address Change (AD01)" },
];

// ── Main page ────────────────────────────────────────────────────────────────

function AppSettings() {
  const [controller, dispatch] = useVisionUIController();
  const {
    transparentSidenav, fixedNavbar, sidenavColor,
    currency, fiscalYearStart, dateFormat, defaultLandingPage,
    notificationBadge, autoRefreshInterval, defaultVatRate,
    serviceDefaults,
  } = controller;

  const { tenantId, isSuperAdmin } = useAuthz();

  const [cs01Fee, setCs01Fee] = useState("");
  const [cs01FeeLoading, setCs01FeeLoading] = useState(false);
  const [cs01FeeSaved, setCs01FeeSaved] = useState(false);
  const [cs01FeeError, setCs01FeeError] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [wideScreen, setWideScreen] = useState(window.innerWidth >= 1440);
  useEffect(() => {
    function check() {
      setDisabled(window.innerWidth <= 1200);
      setWideScreen(window.innerWidth >= 1440);
    }
    window.addEventListener("resize", check);
    check();
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let active = true;
    setCs01FeeLoading(true);
    getSuperAdminConfig()
      .then((config) => {
        if (active) setCs01Fee(((config.visitorCs01FeePence ?? 10000) / 100).toFixed(2));
      })
      .catch((error) => {
        if (active) setCs01FeeError(error?.message || "Failed to load CS01 fee.");
      })
      .finally(() => {
        if (active) setCs01FeeLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isSuperAdmin]);

  const handleSaveCs01Fee = async () => {
    setCs01FeeLoading(true);
    setCs01FeeSaved(false);
    setCs01FeeError("");
    try {
      const response = await updateSuperAdminConfig({
        visitorCs01FeePence: Math.round(parseFloat(cs01Fee || "0") * 100),
      });
      setCs01Fee(((response.visitorCs01FeePence ?? 10000) / 100).toFixed(2));
      setCs01FeeSaved(true);
      setTimeout(() => setCs01FeeSaved(false), 3000);
    } catch (error) {
      setCs01FeeError(error?.message || "Failed to save CS01 fee.");
    } finally {
      setCs01FeeLoading(false);
    }
  };

  // Service defaults state
  const [services, setServices]     = useState([]);
  const [users, setUsers]           = useState([]);
  const [svcLoading, setSvcLoading] = useState(true);

  useEffect(() => {
    async function loadServiceData() {
      setSvcLoading(true);
      try {
        const [msRes, usersRes] = await Promise.all([
          listMasterServices(),
          tenantId ? listTenantUsers({ tenantId }) : Promise.resolve([]),
        ]);
        setServices(msRes.services || []);
        setUsers(usersRes.users || usersRes || []);
      } catch (_) {
        setServices([]);
        setUsers([]);
      } finally {
        setSvcLoading(false);
      }
    }
    loadServiceData();
  }, [tenantId]);

  // Email templates state
  const [tplLoading,  setTplLoading]  = useState(true);
  const [tplSaving,   setTplSaving]   = useState({});
  const [tplMsg,      setTplMsg]      = useState({});
  const [tplSelected, setTplSelected] = useState(EMAIL_TEMPLATE_DEFS[0].key);
  const [tplDraft,    setTplDraft]    = useState({});  // { [key]: { subject, body } }

  useEffect(() => {
    if (!tenantId) return;
    setTplLoading(true);
    getEmailTemplates(tenantId)
      .then((saved) => {
        const draft = {};
        EMAIL_TEMPLATE_DEFS.forEach((def) => {
          draft[def.key] = {
            subject: saved[def.key]?.subject ?? def.defaultSubject,
            body:    saved[def.key]?.body    ?? def.defaultBody,
          };
        });
        setTplDraft(draft);
      })
      .catch(() => {
        const draft = {};
        EMAIL_TEMPLATE_DEFS.forEach((def) => {
          draft[def.key] = { subject: def.defaultSubject, body: def.defaultBody };
        });
        setTplDraft(draft);
      })
      .finally(() => setTplLoading(false));
  }, [tenantId]);

  const handleSaveTpl = async (key) => {
    setTplSaving((p) => ({ ...p, [key]: true }));
    setTplMsg((p) => ({ ...p, [key]: "" }));
    try {
      await saveEmailTemplate(tenantId, key, tplDraft[key]);
      setTplMsg((p) => ({ ...p, [key]: "✓ Saved" }));
      setTimeout(() => setTplMsg((p) => ({ ...p, [key]: "" })), 2500);
    } catch (e) {
      setTplMsg((p) => ({ ...p, [key]: e?.message || "Save failed" }));
    } finally {
      setTplSaving((p) => ({ ...p, [key]: false }));
    }
  };

  const activeTplDef = EMAIL_TEMPLATE_DEFS.find((d) => d.key === tplSelected);
  const activeDraft  = tplDraft[tplSelected] || { subject: "", body: "" };

  // ── Fee Schedule state ───────────────────────────────────────────────────
  const [feeData,   setFeeData]   = useState({});  // { vat: 0, payroll: 0, ... }
  const [feeLoading, setFeeLoading] = useState(true);
  const [feeSaving,  setFeeSaving]  = useState(false);
  const [feeError,   setFeeError]   = useState("");
  const [feeSaved,   setFeeSaved]   = useState(false);
  const [feeUpdatedAt, setFeeUpdatedAt] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    setFeeLoading(true);
    getFeeSchedule(tenantId)
      .then((res) => {
        setFeeData(res.fees || {});
        setFeeUpdatedAt(res.updatedAt || null);
      })
      .catch(() => setFeeData({}))
      .finally(() => setFeeLoading(false));
  }, [tenantId]);

  const handleSaveFees = async () => {
    setFeeSaving(true); setFeeError(""); setFeeSaved(false);
    try {
      const res = await updateFeeSchedule(tenantId, feeData);
      setFeeData(res.fees || feeData);
      setFeeUpdatedAt(res.updatedAt || null);
      setFeeSaved(true);
      setTimeout(() => setFeeSaved(false), 3000);
    } catch (e) {
      setFeeError(e?.message || "Failed to save fees.");
    } finally {
      setFeeSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} px={{ xs: 1, md: 2 }}>

        {/* Page header */}
        <VuiBox mb={4}>
          <VuiTypography variant="h4" fontWeight="bold" mb={0.5} sx={{ color: TC.primary }}>
            Settings
          </VuiTypography>
          <VuiTypography sx={{ color: TC.tertiary, fontSize: "13px" }}>
            Customise your workspace — changes apply instantly
          </VuiTypography>
        </VuiBox>

        <Grid container spacing={3} alignItems="stretch">

          {/* ── Display ───────────────────────────── */}
          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon="palette"
              iconColor="#0075ff"
              title="Display"
              description="Appearance & navigation"
            >
              <SettingRow label="Sidenav Colour">
                <VuiBox display="flex" gap={0.8}>
                  {sidenavColors.map((color) => (
                    <IconButton
                      key={color}
                      title={color}
                      aria-label={`Set sidenav colour to ${color}`}
                      sx={({ palette: { white }, transitions }) => ({
                        width: 26, height: 26, padding: 0,
                        border: `2px solid`,
                        borderColor: sidenavColor === color ? white.main : "transparent",
                        borderRadius: "50%",
                        transition: transitions.create("border-color", {
                          easing: transitions.easing.sharp,
                          duration: transitions.duration.shorter,
                        }),
                        backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                          linearGradient(gradients[color].main, gradients[color].state),
                        "&:hover": { borderColor: white.main },
                      })}
                      onClick={() => setSidenavColor(dispatch, color)}
                    />
                  ))}
                </VuiBox>
              </SettingRow>

              {wideScreen && (
                <SettingRow label="Sidenav Type" hint="Transparent or solid background">
                  <VuiBox display="flex" gap={0.8}>
                    <VuiButton
                      color="info" size="small"
                      variant={transparentSidenav ? "contained" : "outlined"}
                      onClick={() => setTransparentSidenav(dispatch, true)}
                      disabled={disabled}
                      sx={{ fontSize: "11px", px: 1.5, py: 0.6, minWidth: 0 }}
                    >
                      Transparent
                    </VuiButton>
                    <VuiButton
                      color="info" size="small"
                      variant={transparentSidenav ? "outlined" : "contained"}
                      onClick={() => setTransparentSidenav(dispatch, false)}
                      disabled={disabled}
                      sx={{ fontSize: "11px", px: 1.5, py: 0.6, minWidth: 0 }}
                    >
                      Opaque
                    </VuiButton>
                  </VuiBox>
                </SettingRow>
              )}

              <SettingRow label="Fixed Navbar" hint="Pin navbar when scrolling" last>
                <VuiSwitch
                  checked={fixedNavbar}
                  onChange={() => setFixedNavbar(dispatch, !fixedNavbar)}
                  color="info"
                />
              </SettingRow>
            </SectionCard>
          </Grid>

          {/* ── Accounting ────────────────────────── */}
          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon="calculate"
              iconColor="#01b574"
              title="Accounting"
              description="Defaults for financial data"
            >
              <SettingRow label="Currency" hint="Shown across all financial views">
                <VuiBox display="flex" gap={0.8}>
                  {["GBP", "USD", "EUR"].map((c) => (
                    <VuiButton
                      key={c} size="small" color="info"
                      variant={currency === c ? "contained" : "outlined"}
                      onClick={() => setCurrency(dispatch, c)}
                      sx={{ minWidth: 52, fontSize: "11px", px: 1, py: 0.6 }}
                    >
                      {c}
                    </VuiButton>
                  ))}
                </VuiBox>
              </SettingRow>

              <SettingRow label="Default VAT Rate" hint="Pre-filled when creating VAT entries">
                <VuiBox display="flex" gap={0.8}>
                  {[20, 5, 0].map((rate) => (
                    <VuiButton
                      key={rate} size="small" color="info"
                      variant={defaultVatRate === rate ? "contained" : "outlined"}
                      onClick={() => setDefaultVatRate(dispatch, rate)}
                      sx={{ minWidth: 48, fontSize: "11px", px: 1, py: 0.6 }}
                    >
                      {rate}%
                    </VuiButton>
                  ))}
                </VuiBox>
              </SettingRow>

              <SettingRow label="Fiscal Year Start" hint="Month your accounting year begins">
                <Select
                  value={fiscalYearStart}
                  onChange={(e) => setFiscalYearStart(dispatch, e.target.value)}
                  size="small"
                  sx={selectSx}
                >
                  {MONTHS.map((m, i) => (
                    <MenuItem key={i + 1} value={i + 1} sx={{ fontSize: "13px" }}>{m}</MenuItem>
                  ))}
                </Select>
              </SettingRow>

              <SettingRow label="Date Format" last>
                <VuiBox display="flex" gap={0.8}>
                  {["DD/MM/YYYY", "MM/DD/YYYY"].map((fmt) => (
                    <VuiButton
                      key={fmt} size="small" color="info"
                      variant={dateFormat === fmt ? "contained" : "outlined"}
                      onClick={() => setDateFormat(dispatch, fmt)}
                      sx={{ fontSize: "10px", px: 1, py: 0.6, minWidth: 0 }}
                    >
                      {fmt}
                    </VuiButton>
                  ))}
                </VuiBox>
              </SettingRow>
            </SectionCard>
          </Grid>

          {/* ── Behaviour ─────────────────────────── */}
          <Grid item xs={12} md={12} lg={4}>
            <SectionCard
              icon="tune"
              iconColor="#ff6b6b"
              title="Behaviour"
              description="App behaviour & notifications"
            >
              <SettingRow label="Default Landing Page" hint="First page shown after sign-in">
                <Select
                  value={defaultLandingPage}
                  onChange={(e) => setDefaultLandingPage(dispatch, e.target.value)}
                  size="small"
                  sx={selectSx}
                >
                  {(() => {
                    // Build landing options from the routes configuration, excluding dynamic routes with params
                    const flatten = (arr) => arr.reduce((acc, r) => {
                      if (r.collapse) return acc.concat(flatten(r.collapse));
                      if (r.route) acc.push(r);
                      return acc;
                    }, []);
                    const landingOptions = flatten(require("routes").default || require("routes")).filter(r => typeof r.route === 'string' && !r.route.includes(':'));
                    return landingOptions.map((opt) => (
                      <MenuItem key={opt.route} value={opt.route} sx={{ fontSize: "13px" }}>
                        {opt.name || opt.title || opt.route}
                      </MenuItem>
                    ));
                  })()}
                </Select>
              </SettingRow>

              <SettingRow label="Notification Badge" hint="Show unread count on bell icon">
                <VuiSwitch
                  checked={notificationBadge}
                  onChange={() => setNotificationBadge(dispatch, !notificationBadge)}
                  color="info"
                />
              </SettingRow>

              <SettingRow label="Bank Feeds Auto-refresh" hint="Refresh interval for bank connections" last>
                <VuiBox display="flex" gap={0.8}>
                  {[
                    { label: "Off",    value: 0 },
                    { label: "1 min",  value: 60 },
                    { label: "5 min",  value: 300 },
                  ].map((opt) => (
                    <VuiButton
                      key={opt.value} size="small" color="info"
                      variant={autoRefreshInterval === opt.value ? "contained" : "outlined"}
                      onClick={() => setAutoRefreshInterval(dispatch, opt.value)}
                      sx={{ fontSize: "11px", px: 1.2, py: 0.6, minWidth: 0 }}
                    >
                      {opt.label}
                    </VuiButton>
                  ))}
                </VuiBox>
              </SettingRow>
            </SectionCard>
          </Grid>

          {isSuperAdmin && (
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard
                icon="payments"
                iconColor="#7b61ff"
                title="Visitor CS01 Fee"
                description="Set the public CS01 portal fee charged to visitor submissions."
              >
                <VuiTypography sx={{ color: TC.secondary, fontSize: FS.xs, fontWeight: FW.semiBold, display: "block", mb: 0.8 }}>
                  Fee (£)
                </VuiTypography>
                <TextField
                  fullWidth
                  size="small"
                  value={cs01Fee}
                  onChange={(e) => setCs01Fee(e.target.value)}
                  placeholder="100.00"
                  type="number"
                  inputProps={{ min: 1, step: 0.01 }}
                  sx={{ mb: 1.5 }}
                />
                {cs01FeeError && (
                  <VuiTypography sx={{ color: TC.error, fontSize: FS.sm, mb: 1 }}>
                    ⚠ {cs01FeeError}
                  </VuiTypography>
                )}
                <VuiBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <VuiButton size="small" color="info" onClick={handleSaveCs01Fee} disabled={cs01FeeLoading || !cs01Fee}>
                    {cs01FeeLoading ? "Saving..." : "Save Fee"}
                  </VuiButton>
                  {cs01FeeSaved && <VuiTypography sx={{ color: TC.success, fontSize: FS.sm }}>✓ Saved</VuiTypography>}
                </VuiBox>
              </SectionCard>
            </Grid>
          )}

          {/* ── Service Defaults ──────────────────── */}
          <Grid item xs={12}>
            <SectionCard
              icon="assignment_ind"
              iconColor="#f7b731"
              title="Service Defaults"
              description="Default assignee for each service — auto-filled when assigning a service to any client (can always be overridden)"
            >
              {svcLoading ? (
                <VuiBox display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} sx={{ color: TC.secondary }} />
                </VuiBox>
              ) : services.length === 0 ? (
                <VuiTypography sx={{ color: TC.secondary, fontSize: "13px", py: 2, textAlign: "center" }}>
                  No services configured yet.
                </VuiTypography>
              ) : (
                <Grid container spacing={0}>
                  {services.map((svc, idx) => {
                    const def = serviceDefaults?.[svc.id];
                    const isLast = idx === services.length - 1;
                    return (
                      <Grid item xs={12} key={svc.id}>
                        <VuiBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                          py={1.6}
                          sx={{ flexWrap: "wrap" }}
                        >
                          {/* Service name */}
                          <VuiBox sx={{ minWidth: 0, flex: 1 }}>
                            <VuiTypography sx={{ color: TC.primary, fontSize: "13px", fontWeight: 600 }}>
                              {svc.name}
                            </VuiTypography>
                            {svc.description && (
                              <VuiTypography sx={{ color: TC.secondary, fontSize: "11px", mt: 0.2 }}>
                                {svc.description}
                              </VuiTypography>
                            )}
                          </VuiBox>

                          {/* Assignee picker */}
                          <VuiBox display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                            {def && (
                              <Chip
                                label={def.name}
                                size="small"
                                onDelete={() => setServiceDefault(dispatch, svc.id, null, null)}
                                sx={{
                                  bgcolor: "rgba(0,117,255,0.15)",
                                  color: TC.primary,
                                  fontSize: "11px",
                                  border: "1px solid rgba(0,117,255,0.35)",
                                  "& .MuiChip-deleteIcon": { color: TC.secondary, "&:hover": { color: "#fff" } },
                                }}
                              />
                            )}
                            <Select
                              value=""
                              displayEmpty
                              size="small"
                              onChange={(e) => {
                                const u = users.find((u) => u.uid === e.target.value);
                                if (u) setServiceDefault(dispatch, svc.id, u.uid, u.displayName || u.email);
                              }}
                              sx={{
                                color: `${TC.primary} !important`,
                                fontSize: "12px",
                                minWidth: 140,
                                background: "#f4f8ff",
                                border: "1px solid #d8e6f7",
                                borderRadius: "6px",
                                ".MuiOutlinedInput-notchedOutline": { border: "none" },
                                ".MuiSvgIcon-root": { color: TC.secondary },
                                ".MuiSelect-select": { py: "6px", px: "10px" },
                              }}
                              renderValue={() => def ? "Change…" : "Assign default…"}
                            >
                              {users.length === 0 ? (
                                <MenuItem disabled sx={{ fontSize: "12px" }}>No users found</MenuItem>
                              ) : (
                                users.map((u) => (
                                  <MenuItem key={u.uid} value={u.uid} sx={{ fontSize: "12px" }}>
                                    {u.displayName || u.email}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </VuiBox>
                        </VuiBox>
                        {!isLast && <Divider sx={{ borderColor: "#d8e6f7", m: 0 }} />}
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </SectionCard>
          </Grid>

          {/* ── Fee Schedule ──────────────────────── */}
          <Grid item xs={12}>
            <SectionCard
              icon="receipt_long"
              iconColor="#0057d8"
              title="Fee Schedule"
              description="Standard fees charged per service — applied when generating charges from invoiced jobs. Stored per tenant."
            >
              {feeLoading ? (
                <VuiBox display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} sx={{ color: TC.secondary }} />
                </VuiBox>
              ) : (
                <>
                  <Grid container spacing={2} mb={2.5}>
                    {FEE_SERVICE_DEFS.map((svc) => (
                      <Grid item xs={12} sm={6} md={3} key={svc.id}>
                        <VuiTypography sx={{ color: TC.secondary, fontSize: FS.xs, fontWeight: FW.semiBold, display: "block", mb: 0.6 }}>
                          {svc.label}
                        </VuiTypography>
                        <VuiBox display="flex" alignItems="center" gap={0.5}
                          sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: "8px", px: 1.2, py: 0.5 }}>
                          <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm, fontWeight: FW.semiBold }}>£</VuiTypography>
                          <input
                            type="number" min="0" step="0.01"
                            value={feeData[svc.id] ?? 0}
                            onChange={(e) => setFeeData((prev) => ({
                              ...prev,
                              [svc.id]: parseFloat(e.target.value) || 0,
                            }))}
                            style={{
                              flex: 1, border: "none", background: "transparent", outline: "none",
                              color: TC.primary, fontSize: FS.md, fontFamily: "inherit",
                              padding: "4px 0",
                            }}
                          />
                        </VuiBox>
                      </Grid>
                    ))}
                  </Grid>

                  {feeError && (
                    <VuiTypography sx={{ color: TC.error, fontSize: FS.sm, display: "block", mb: 1.5 }}>
                      ⚠ {feeError}
                    </VuiTypography>
                  )}

                  <VuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <VuiButton size="small" color="info" variant="contained"
                      disabled={feeSaving || !tenantId}
                      onClick={handleSaveFees}
                      sx={{ px: 2.5 }}>
                      {feeSaving
                        ? <><CircularProgress size={12} color="inherit" sx={{ mr: 0.8 }} />Saving…</>
                        : "Save Fee Schedule"}
                    </VuiButton>
                    {feeSaved && (
                      <VuiTypography sx={{ color: TC.success, fontSize: FS.sm }}>✓ Saved</VuiTypography>
                    )}
                    {feeUpdatedAt && !feeSaved && (
                      <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.xs }}>
                        Last saved {new Date(feeUpdatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </VuiTypography>
                    )}
                    <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.xs, ml: "auto" }}>
                      These fees apply to all clients for this practice. Override per-client is not yet supported.
                    </VuiTypography>
                  </VuiBox>
                </>
              )}
            </SectionCard>
          </Grid>

          {/* ── Email Templates ───────────────────── */}
          <Grid item xs={12}>
            <SectionCard
              icon="email"
              iconColor="#0075ff"
              title="Email Templates"
              description="Default subject and body for client emails — use {{company}} and {{address}} as placeholders"
            >
              {tplLoading ? (
                <VuiBox display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} sx={{ color: TC.secondary }} />
                </VuiBox>
              ) : (
                <>
                  {/* Template picker */}
                  <VuiBox display="flex" gap={1} mb={2.5} sx={{ flexWrap: "wrap" }}>
                    {EMAIL_TEMPLATE_DEFS.map((def) => (
                      <VuiButton
                        key={def.key} size="small"
                        variant={tplSelected === def.key ? "contained" : "outlined"}
                        color="info"
                        onClick={() => setTplSelected(def.key)}
                        sx={{ fontSize: "11px", px: 1.5, py: 0.7 }}
                      >
                        {def.label}
                      </VuiButton>
                    ))}
                  </VuiBox>

                  {/* Subject field */}
                  <VuiTypography sx={{ fontSize: "12px", fontWeight: 600, color: TC.primary, mb: 0.8 }}>
                    Subject
                  </VuiTypography>
                  <div style={{ width: "100%", marginBottom: 16 }}>
                    <TextField
                      fullWidth size="medium"
                      value={activeDraft.subject}
                      onChange={(e) => setTplDraft((p) => ({ ...p, [tplSelected]: { ...p[tplSelected], subject: e.target.value } }))}
                      placeholder="Email subject…"
                      style={{ width: "100%" }}
                      sx={{ "& input": { width: "100% !important" } }}
                    />
                  </div>

                  {/* Body field */}
                  <VuiTypography sx={{ fontSize: "12px", fontWeight: 600, color: TC.primary, mb: 0.8 }}>
                    Message Body
                  </VuiTypography>
                  <div style={{ width: "100%", marginBottom: 16 }}>
                    <TextField
                      fullWidth multiline rows={9}
                      value={activeDraft.body}
                      onChange={(e) => setTplDraft((p) => ({ ...p, [tplSelected]: { ...p[tplSelected], body: e.target.value } }))}
                      placeholder="Email body…"
                      style={{ width: "100%" }}
                      sx={{ "& textarea": { width: "100% !important" }, "& .MuiInputBase-root": { lineHeight: 1.6, fontSize: "0.88rem" } }}
                    />
                  </div>

                  {/* Placeholders hint */}
                  <VuiBox mb={2}>
                    <VuiTypography sx={{ fontSize: "11px", color: TC.secondary }}>
                      Placeholders: <code style={{ background: "#f0f5ff", borderRadius: 4, padding: "1px 5px" }}>{"{{company}}"}</code> = company legal name &nbsp;·&nbsp; <code style={{ background: "#f0f5ff", borderRadius: 4, padding: "1px 5px" }}>{"{{address}}"}</code> = registered address
                    </VuiTypography>
                  </VuiBox>

                  {/* Save row */}
                  <VuiBox display="flex" alignItems="center" gap={2}>
                    <VuiButton size="small" color="info" variant="contained"
                      disabled={!!tplSaving[tplSelected]}
                      onClick={() => handleSaveTpl(tplSelected)}
                      sx={{ px: 2.5 }}
                    >
                      {tplSaving[tplSelected]
                        ? <><CircularProgress size={12} color="inherit" sx={{ mr: 0.8 }} />Saving…</>
                        : "Save Template"}
                    </VuiButton>
                    <VuiButton size="small" variant="outlined" color="secondary"
                      onClick={() => setTplDraft((p) => ({
                        ...p,
                        [tplSelected]: {
                          subject: activeTplDef.defaultSubject,
                          body:    activeTplDef.defaultBody,
                        },
                      }))}
                      sx={{ px: 2 }}
                    >
                      Reset to Default
                    </VuiButton>
                    {tplMsg[tplSelected] && (
                      <VuiTypography sx={{
                        fontSize: "12px",
                        color: tplMsg[tplSelected].startsWith("✓") ? "#4caf50" : "#f44336",
                      }}>
                        {tplMsg[tplSelected]}
                      </VuiTypography>
                    )}
                  </VuiBox>
                </>
              )}
            </SectionCard>
          </Grid>

        </Grid>
      </VuiBox>
    </DashboardLayout>
  );
}

export default AppSettings;


